import { useMemo } from 'react';

import { graphql } from '@/__generated__';
import { ResourceNodeType } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';

export const GetAppliedMaterializedFields = graphql(/* GraphQL */ `
  query AppliedMaterializedFields(
    $environmentId: BigInt!
    $userId: BigInt
    $types: [ResourceNodeType!]!
  ) {
    environment(id: $environmentId, userId: $userId) {
      dbtProjectName
      applied {
        lastUpdatedAt
        lineage(filter: { types: $types }) {
          database
          schema
        }
      }
    }
  }
`);

type AppliedMaterializedFieldsParams = {
  environmentId: number | undefined;
  userId: number | 'loading' | null;
  resourceTypes: ResourceNodeType[];
  pause?: boolean;
};

export type MaterializedFields = {
  databases: string[];
  schemas: string[];
};

export const useAppliedMaterializedFields = ({
  environmentId = 0,
  userId,
  resourceTypes,
  pause,
}: AppliedMaterializedFieldsParams): MaterializedFields => {
  const query = useQueryStable({
    query: GetAppliedMaterializedFields,
    variables: {
      environmentId,
      userId: userId as number | null,
      types: resourceTypes,
    },
    pause: pause || !environmentId || userId === 'loading',
  });

  useCacheMonitor({ applied: query[0].data?.environment.applied?.lastUpdatedAt });

  const { databases, schemas } = useMemo(() => {
    if (query[0].error || query[0].fetching) {
      return [];
    }
    const nodes = query[0].data?.environment.applied?.lineage;
    const databasesSet = new Set<string>();
    const schemasSet = new Set<string>();
    nodes?.forEach(({ database, schema }) => {
      if (database) databasesSet.add(database);
      if (schema) schemasSet.add(schema);
    });

    return {
      databases: Array.from(databasesSet).sort(),
      schemas: Array.from(schemasSet).sort(),
    };
  }, [query]) as MaterializedFields;

  return { databases, schemas };
};
